import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { needsResearchConsent } from '../../lib/consent';

/**
 * The only paths a doctor or admin may reach; everything else sends them back
 * to /dashboard.
 *
 * This list IS the access control — with RLS excluded (URS A1) nothing else
 * separates a clinician's screens from a patient's. Adding a path here changes
 * the security posture of the app, so each one is a deliberate decision:
 *
 *   /dashboard       the clinician's own screen
 *   /about           static content
 *   /monthly-report  FR-16, read-only, keyed by ?patient= — the requirement is
 *                    explicitly that a doctor can pull a patient's report, and
 *                    the Edge Function that builds one re-checks that the
 *                    patient is at a hospital the doctor is assigned to.
 */
const CLINICIAN_ROUTES = ['/dashboard', '/about', '/monthly-report'];

interface ProtectedRouteProps {
  children: JSX.Element;
  requireProfile?: boolean;
}

export const ProtectedRoute = ({ children, requireProfile = true }: ProtectedRouteProps) => {
  const { user, profile, loading, authTimedOut } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl font-bold text-gray-600">Memuat...</div>
      </div>
    );
  }

  // A connectivity failure is not a signed-out user. Saying so is the single
  // most useful DF-02 change: "it said the connection failed" is diagnosable,
  // "it logged me out" is not.
  if (authTimedOut && !user) {
    return (
      <div className="screen min-h-[100dvh] h-full">
        <div className="screen-card text-center">
          <div className="section-title">Gagal memuat sesi</div>
          <div className="subtitle">
            Periksa koneksi internet kamu, lalu coba lagi.
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => window.location.reload()}
          >
            Muat ulang
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    // Carry the attempted route so Login can return the user to it, instead of
    // dropping them on the home screen and losing where they were.
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (requireProfile && !profile) {
    return <Navigate to="/complete-profile" replace />;
  }

  // If user has profile but tries to access complete-profile page, redirect to home
  if (!requireProfile && profile) {
    return <Navigate to="/" replace />;
  }

  if (profile) {
    const isDoctorOrAdmin = profile.role === 'doctor' || profile.role === 'superadmin';
    const isPatient = profile.role === 'patient';

    if (isDoctorOrAdmin && !CLINICIAN_ROUTES.includes(location.pathname)) {
      return <Navigate to="/dashboard" replace />;
    }

    if (isPatient && location.pathname === '/dashboard') {
      return <Navigate to="/" replace />;
    }

    // FR-22: research consent is asked on first use, and again whenever the
    // consent text changes version. Only online — the answer cannot be saved
    // offline, and holding a child at this screen with no network would stop
    // them recording an injection. They are asked on the next online visit.
    if (
      needsResearchConsent(profile) &&
      location.pathname !== '/consent' &&
      (typeof navigator === 'undefined' || navigator.onLine !== false)
    ) {
      return (
        <Navigate
          to="/consent"
          replace
          state={{ from: location.pathname + location.search }}
        />
      );
    }
  }

  return children;
};
